import { useEffect } from 'react'
import AddonStepModal from '@/components/screens/AddonStepModal'
import CartScreen from '@/components/screens/CartScreen'
import CheckoutScreen from '@/components/screens/CheckoutScreen'
import LoginScreen from '@/components/screens/LoginScreen'
import MenuTypeSelect from '@/components/screens/MenuTypeSelect'
import PayingScreen from '@/components/screens/PayingScreen'
import ProductGrid from '@/components/screens/ProductGrid'
import ProductModal from '@/components/screens/ProductModal'
import ReceiptScreen from '@/components/screens/ReceiptScreen'
import InactivityOverlay from '@/components/ui/InactivityOverlay'
import KioskHeader from '@/components/ui/KioskHeader'
import { useAutoUpdate } from '@/hooks/useAutoUpdate'
import { useInactivity } from '@/hooks/useInactivity'
import { gql } from '@/lib/api'
import { parseTillAttributes } from '@/lib/kiosk-config'
import { ME_QUERY, TILL_QUERY } from '@/lib/queries'
import type { KioskConfig } from '@/lib/types'
import { useKioskStore } from '@/stores/kiosk-store'

const TOKEN_KEY = 'kiosk_token'

interface KioskShellProps {
  restaurantId: string
  tillId: string
  tillName?: string
  apiEndpoint: string
  locale: string
}

function applyRemoteTheme(cfg: import('@/lib/kiosk-config').KioskRemoteConfig) {
  const root = document.documentElement
  if (cfg.colorAccent) root.style.setProperty('--kiosk-accent', cfg.colorAccent)
  if (cfg.colorAccentHover) root.style.setProperty('--kiosk-accent-hover', cfg.colorAccentHover)
  if (cfg.colorBg) root.style.setProperty('--kiosk-bg', cfg.colorBg)
  if (cfg.colorSurface) root.style.setProperty('--kiosk-surface', cfg.colorSurface)
  if (cfg.colorSurface2) root.style.setProperty('--kiosk-surface-2', cfg.colorSurface2)
  if (cfg.colorText) root.style.setProperty('--kiosk-text', cfg.colorText)
  if (cfg.colorMuted) root.style.setProperty('--kiosk-muted', cfg.colorMuted)
}

// Fetch till config and store showsStripeSwish + posTerminalId
async function fetchTillConfig(apiEndpoint: string, token: string, restaurantId: string, tillId: string) {
  try {
    const data = await gql(apiEndpoint, TILL_QUERY, { restaurantId, tillId }, token)
    const till = data?.restaurant?.till
    if (till) {
      const store = useKioskStore.getState()
      // Save till name from LocoPOS so admin modal shows the actual name
      if (till.name) store.setTillName(till.name)
      store.setShowsStripeSwish(till.showsStripeSwish ?? false)
      store.setShowsTip(till.showsTip ?? false)
      store.setShowsMenuType(till.showsMenuType ?? 'All')
      // Pick the first ONLINE terminal — whichever provider is actually connected.
      // This correctly handles both Stripe restaurants (Kungshallen, MoS) and Nets
      // restaurants (EAT Market) without depending on which paymentMethod options exist.
      // Also store the full list so the admin modal can show diagnostics for offline terminals.
      const allTerminals = till.posTerminals || []
      store.setPosTerminals(allTerminals)
      const stripeTerminal = allTerminals.find((t: { isOnline: boolean; paymentProvider: string }) => t.isOnline && t.paymentProvider === 'Stripe')
      const netsTerminal = allTerminals.find((t: { isOnline: boolean; paymentProvider: string }) => t.isOnline && t.paymentProvider === 'NetsConnectCloud')
      const terminal = stripeTerminal || netsTerminal
      if (terminal) {
        store.setPosTerminalId(terminal.id)
        store.setPosTerminalProvider(terminal.paymentProvider)
      } else {
        store.setPosTerminalId(null)
        store.setPosTerminalProvider(null)
      }
      // Use the till's receipt printer queue ID
      // receiptPrinterId in OrderIntentInput refers to a PrinterQueue (not PhysicalPrinter)
      // since OrderIntent.receiptPrinter is a PrinterQueue
      if (till.receiptPrinterQueue?.id) {
        store.setReceiptPrinterId(till.receiptPrinterQueue.id)
      }
      // Store kitchen routing setting (Always/Ask/Never) so we set
      // productsHandedToCustomer correctly in the order intent
      if (till.sendOrderToKitchen) {
        store.setSendOrderToKitchen(till.sendOrderToKitchen)
      }
      // Update payment methods from till (more specific than restaurant-level)
      if (till.paymentMethods?.length > 0) store.setPaymentMethods(till.paymentMethods)
      // Parse pong.* attributes and apply remote config + theme overrides
      const remote = parseTillAttributes(till)
      store.setRemoteConfig(remote)
      applyRemoteTheme(remote)
      applyPwaManifest(remote)
    }
  } catch {
    /* till query failed — non-critical, user might not have access */
  }
}

/**
 * Bygger en dynamisk PWA-manifest från till-attributen och injicerar den
 * via Blob URL. Detta låter varje kund (Pong, Kebabdudes, ...) ha eget
 * namn och egna ikoner på iPaden utan att vi behöver bygga om kiosken.
 *
 * VIKTIG GOTCHA: iOS Safari läser manifest:et VID "Lägg till på hemskärmen"-
 * tillfället. Om manifestet uppdateras EFTER att user redan har installerat
 * PWAn, så krävs av-/återinstallation för att icon ska ändras. För FÖRSTA
 * installationen funkar det dock direkt eftersom till-config laddats innan
 * user trycker Share → Lägg till.
 */
function applyPwaManifest(cfg: import('@/lib/kiosk-config').KioskRemoteConfig) {
  if (typeof document === 'undefined') return

  const themeColor = cfg.pwaThemeColor || cfg.colorAccent || '#D4872C'
  const bgColor = cfg.colorBg || '#0A0A0A'
  const name = cfg.appName || 'Kiosk'
  const icon192 = cfg.appIconUrl192 || '/images/icon-192.png'
  const icon512 = cfg.appIconUrl512 || '/images/icon-512.png'

  const manifest = {
    name,
    short_name: name,
    description: 'Självbetjäningskassa',
    start_url: '/',
    display: 'standalone',
    orientation: 'any',
    background_color: bgColor,
    theme_color: themeColor,
    icons: [
      { src: icon192, sizes: '192x192', type: 'image/png' },
      { src: icon512, sizes: '512x512', type: 'image/png' },
    ],
  }

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
  const url = URL.createObjectURL(blob)
  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'manifest'
    document.head.appendChild(link)
  }
  // Frigör tidigare blob-URL för att undvika minnesläckage vid omkonfig
  if (link.href.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(link.href)
    } catch {
      /* ignore */
    }
  }
  link.href = url

  // iOS apple-touch-icon (för "Lägg till på hemskärmen" innan PWA installerats)
  const appleIcons = document.querySelectorAll<HTMLLinkElement>('link[rel="apple-touch-icon"]')
  appleIcons.forEach((l) => {
    l.remove()
  })
  const appleLink = document.createElement('link')
  appleLink.rel = 'apple-touch-icon'
  appleLink.href = icon192
  document.head.appendChild(appleLink)

  // theme_color påverkar Safari status bar
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  meta.content = themeColor
}

// Export for use in KioskHeader login. The fast-refresh plugin would prefer
// this lived in its own module, but the function is tightly coupled to the
// rest of this file (parseTillAttributes, applyRemoteTheme, applyPwaManifest)
// and not worth splitting out for a dev-only HMR concern.
// eslint-disable-next-line react-refresh/only-export-components
export { fetchTillConfig }

export default function KioskShell({ restaurantId, tillId, tillName, apiEndpoint, locale }: KioskShellProps) {
  const step = useKioskStore((s) => s.step)
  const config = useKioskStore((s) => s.config)
  const error = useKioskStore((s) => s.error)
  const setConfig = useKioskStore((s) => s.setConfig)
  const setParseToken = useKioskStore((s) => s.setParseToken)
  const setTillName = useKioskStore((s) => s.setTillName)
  const setError = useKioskStore((s) => s.setError)
  const setStep = useKioskStore((s) => s.setStep)
  const { showWarning, countdown, dismiss } = useInactivity()
  useAutoUpdate()

  // Boot sequence. Zustand setters are referentially stable and `tillName` is
  // a one-shot init value, so we intentionally only re-boot on identity changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    let cancelled = false

    async function boot() {
      try {
        if (cancelled) return

        const kioskConfig: KioskConfig = {
          restaurantId,
          tillId,
          apiEndpoint,
          locale,
        }

        setConfig(kioskConfig)
        if (tillName) setTillName(tillName)

        // Token — kiosken kräver staff-login. Sparad token i localStorage
        //    återanvänds (verifieras via ME_QUERY); annars visas login-skärmen,
        //    som sparar tokenen permanent vid lyckad inloggning.
        const savedToken = localStorage.getItem(TOKEN_KEY)
        let validToken: string | null = null
        if (savedToken) {
          try {
            await gql(apiEndpoint, ME_QUERY, undefined, savedToken)
            validToken = savedToken
          } catch {
            localStorage.removeItem(TOKEN_KEY)
          }
        }

        if (validToken && !cancelled) {
          setParseToken(validToken)
          await fetchTillConfig(apiEndpoint, validToken, restaurantId, tillId)
        } else if (!cancelled) {
          setStep('login')
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Kunde inte starta kiosken')
          setStep('error')
        }
      }
    }

    boot()
    return () => {
      cancelled = true
    }
  }, [restaurantId, tillId, apiEndpoint, locale])

  // Error screen
  if (step === 'error') {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-kiosk-bg px-8 text-center">
        <h1 className="text-2xl font-bold text-red-400 mb-4">Något gick fel</h1>
        <p className="text-kiosk-muted mb-8 max-w-md">{error}</p>
        <button
          onClick={() => {
            setError(null)
            setStep('menuTypeSelect')
          }}
          className="kiosk-btn px-8 py-4 bg-kiosk-accent text-kiosk-bg font-bold rounded-2xl text-lg"
        >
          Försök igen
        </button>
      </div>
    )
  }

  // Loading — waiting for config
  if (!config) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-kiosk-bg">
        <div className="kiosk-spinner" />
      </div>
    )
  }

  // First-time-login screen — visas bara en gång per enhet. Efter login
  // sparas token permanent i localStorage → boot hittar den nästa gång.
  if (step === 'login') {
    return <LoginScreen />
  }

  const showHeader = step !== 'menuTypeSelect'

  return (
    <div className="h-screen w-screen overflow-hidden relative flex flex-col">
      {showHeader && <KioskHeader />}
      <div className="flex-1 overflow-hidden relative">
        {step === 'menuTypeSelect' && <MenuTypeSelect />}
        {step === 'browsing' && <ProductGrid />}
        {step === 'cart' && <CartScreen />}
        {step === 'checkout' && <CheckoutScreen />}
        {step === 'paying' && <PayingScreen />}
        {step === 'receipt' && <ReceiptScreen />}
      </div>

      {/* Product detail modal overlay */}
      <ProductModal />
      <AddonStepModal />

      {/* Inactivity warning */}
      {showWarning && <InactivityOverlay countdown={countdown} onDismiss={dismiss} />}
    </div>
  )
}
